import { useEffect, useState } from 'react';
import { Modal, StyleSheet, View } from 'react-native';
import { Button } from '../../../components/primitives/Button';
import { FormInput } from '../../../components/primitives/FormInput';
import { Select } from '../../../components/primitives/Select';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Body, Heading4, Label } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { ExperimentOutcome } from '../experiments-api';
import { useCreateExperiment } from '../hooks/useExperiments';
import type { VoiceAgentVersion } from '../types';

const DEFAULT_SPLIT = 50;
const DEFAULT_OUTCOME: ExperimentOutcome = 'meeting_booked';

const OUTCOME_TARGETS: ExperimentOutcome[] = [
  'meeting_booked',
  'callback_requested',
  'qualified',
  'disqualified',
  'voicemail',
  'no_answer',
  'do_not_call',
  'other',
];

interface CreateExperimentFormProps {
  agentId: string;
  organizationId: string;
  versions: VoiceAgentVersion[];
  visible: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

/**
 * The pure form content, rendered without a `Modal` wrapper so it can be
 * exercised directly in tests — react-test-renderer cannot mount a
 * react-native `Modal` while it is open (see PhoneNumberInput's
 * CountryPickerList for the established pattern in this repo). Only
 * `CreateExperimentModal` (below) wraps this in the actual `Modal`.
 */
export function CreateExperimentForm({
  agentId,
  organizationId,
  versions,
  visible,
  onClose,
  onCreated,
}: Readonly<CreateExperimentFormProps>) {
  const { t } = useTranslation();
  const create = useCreateExperiment(agentId);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [variantAVersionId, setVariantAVersionId] = useState('');
  const [variantBVersionId, setVariantBVersionId] = useState('');
  const [splitPercent, setSplitPercent] = useState(String(DEFAULT_SPLIT));
  const [outcomeTarget, setOutcomeTarget] = useState<ExperimentOutcome>(DEFAULT_OUTCOME);

  useEffect(() => {
    if (visible) {
      setName('');
      setDescription('');
      setVariantAVersionId('');
      setVariantBVersionId('');
      setSplitPercent(String(DEFAULT_SPLIT));
      setOutcomeTarget(DEFAULT_OUTCOME);
      create.reset();
    }
  }, [visible]);

  const parsedSplit = Number.parseInt(splitPercent, 10);
  const isValid =
    name.trim().length > 0 &&
    !!variantAVersionId &&
    !!variantBVersionId &&
    variantAVersionId !== variantBVersionId &&
    Number.isFinite(parsedSplit) &&
    parsedSplit > 0 &&
    parsedSplit < 100;

  const versionOptions = versions.map((v) => ({ value: v.id, label: `v${v.version}` }));
  const outcomeOptions = OUTCOME_TARGETS.map((value) => ({
    value,
    label: t(`voiceAgents.experiments.outcomeOptions.${value}`),
  }));

  function handleSubmit() {
    if (!isValid) return;
    create.mutate(
      {
        organizationId,
        name: name.trim(),
        description: description.trim() ? description.trim() : undefined,
        variantAVersionId,
        variantBVersionId,
        splitPercent: parsedSplit,
        outcomeTarget,
      },
      {
        onSuccess: () => {
          onCreated?.();
          onClose();
        },
      },
    );
  }

  if (!visible) return null;

  return (
    <View style={styles.overlay}>
      <View
        style={styles.card}
        accessible
        accessibilityLabel={t('voiceAgents.experiments.createTitle')}
      >
        <Heading4 style={styles.title} accessibilityRole="header">
          {t('voiceAgents.experiments.createTitle')}
        </Heading4>

        <VStack spacing="md" style={{ marginTop: tokens.spacing.md }}>
          <VStack spacing="xs">
            <Label style={styles.label}>{t('voiceAgents.experiments.nameLabel')}</Label>
            <FormInput
              value={name}
              onChangeText={setName}
              placeholder={t('voiceAgents.experiments.namePlaceholder')}
              style={styles.input}
              accessibilityLabel={t('voiceAgents.experiments.nameLabel')}
            />
          </VStack>

          <VStack spacing="xs">
            <Label style={styles.label}>{t('voiceAgents.experiments.descriptionLabel')}</Label>
            <FormInput
              value={description}
              onChangeText={setDescription}
              placeholder={t('voiceAgents.experiments.descriptionPlaceholder')}
              style={styles.input}
              multiline
              accessibilityLabel={t('voiceAgents.experiments.descriptionLabel')}
            />
          </VStack>

          <VStack spacing="xs">
            <Label style={styles.label}>{t('voiceAgents.experiments.variantALabel')}</Label>
            <Select
              variant="chips"
              options={versionOptions}
              value={variantAVersionId}
              onChange={setVariantAVersionId}
              testID="create-experiment-variant-a"
            />
          </VStack>

          <VStack spacing="xs">
            <Label style={styles.label}>{t('voiceAgents.experiments.variantBLabel')}</Label>
            <Select
              variant="chips"
              options={versionOptions}
              value={variantBVersionId}
              onChange={setVariantBVersionId}
              testID="create-experiment-variant-b"
            />
          </VStack>

          <VStack spacing="xs">
            <Label style={styles.label}>{t('voiceAgents.experiments.splitLabel')}</Label>
            <FormInput
              value={splitPercent}
              onChangeText={setSplitPercent}
              keyboardType="number-pad"
              style={styles.input}
              accessibilityLabel={t('voiceAgents.experiments.splitLabel')}
            />
          </VStack>

          <VStack spacing="xs">
            <Label style={styles.label}>{t('voiceAgents.experiments.outcomeTargetLabel')}</Label>
            <Select
              variant="chips"
              options={outcomeOptions}
              value={outcomeTarget}
              onChange={(value) => setOutcomeTarget(value as ExperimentOutcome)}
              testID="create-experiment-outcome-target"
            />
          </VStack>
        </VStack>

        {create.isError ? (
          <Body style={styles.error} accessibilityLiveRegion="assertive" accessibilityRole="alert">
            {create.error?.message ?? t('voiceAgents.experiments.errorPrefix')}
          </Body>
        ) : null}

        <HStack spacing="md" style={styles.footer}>
          <Button
            variant="outline"
            size="sm"
            onPress={onClose}
            disabled={create.isPending}
            accessibilityLabel={t('voiceAgents.experiments.cancel')}
          >
            {t('voiceAgents.experiments.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onPress={handleSubmit}
            loading={create.isPending}
            disabled={!isValid || create.isPending}
            accessibilityLabel={t('voiceAgents.experiments.createA11y')}
          >
            {t('voiceAgents.experiments.create')}
          </Button>
        </HStack>
      </View>
    </View>
  );
}

type CreateExperimentModalProps = CreateExperimentFormProps;

/**
 * Wraps `CreateExperimentForm` in the actual react-native `Modal` for
 * runtime use. Not exercised directly in tests — see `CreateExperimentForm`.
 */
export function CreateExperimentModal(props: Readonly<CreateExperimentModalProps>) {
  return (
    <Modal visible={props.visible} transparent animationType="fade" onRequestClose={props.onClose}>
      <CreateExperimentForm {...props} />
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacing.lg,
  },
  card: {
    width: '100%',
    maxWidth: 480,
    maxHeight: '90%',
    backgroundColor: colors.background.elevated,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.muted,
    padding: tokens.spacing.lg,
  },
  title: {
    color: colors.text.primary,
  },
  label: {
    color: colors.text.secondary,
  },
  input: {
    backgroundColor: colors.background.input,
    borderWidth: 1,
    borderColor: colors.border.input,
    borderRadius: tokens.radius.md,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
    minHeight: 44,
  },
  error: {
    color: colors.status.error.main,
    marginTop: tokens.spacing.sm,
    lineHeight: 20,
  },
  footer: {
    marginTop: tokens.spacing.lgPlus,
    justifyContent: 'flex-end',
  },
});
